// Cliente-side analytics: gera IDs persistentes e envia eventos.
import { useEffect, useRef } from "react";
import { trackEvent } from "@/lib/analytics.functions";

const ANON_KEY = "p101.anon";
const SESSION_KEY = "p101.session";

function uuid() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getAnon(): string {
  if (typeof window === "undefined") return "";
  let v = localStorage.getItem(ANON_KEY);
  if (!v) {
    v = uuid();
    localStorage.setItem(ANON_KEY, v);
  }
  return v;
}
function getSession(): string {
  if (typeof window === "undefined") return "";
  let v = sessionStorage.getItem(SESSION_KEY);
  if (!v) {
    v = uuid();
    sessionStorage.setItem(SESSION_KEY, v);
  }
  return v;
}

export type TrackEventName =
  | "landing_view"
  | "page_view"
  | "session_ping"
  | "signup_started"
  | "signup_completed"
  | "checkout_started"
  | "checkout_completed"
  | "subscription_activated"
  | "business_view"
  | "cta_click"
  | "mission_started"
  | "mission_resumed"
  | "mission_completed"
  | "next_mission_opened"
  | "journey_completed"
  | "project_home_viewed"
  | "primary_cta_clicked"
  | "construction_map_opened"
  | "business_block_reviewed"
  // Wave 3 — biblioteca inteligente
  | "library_business_viewed"
  | "methodology_previewed"
  | "business_build_started"
  | "methodology_phase_started"
  | "methodology_phase_completed"
  | "practical_action_viewed"
  | "calculation_completed"
  // HV.1 — Homologação do Business Builder
  | "builder_session_started"
  | "builder_step_started"
  | "builder_step_completed"
  | "builder_step_abandoned"
  | "builder_next_step_clicked"
  | "builder_plan_viewed"
  | "builder_pending_viewed"
  | "builder_summary_viewed"
  | "builder_section_revisited"
  | "builder_feedback_submitted";

export function track(event_name: TrackEventName, extra?: {
  path?: string;
  business_slug?: string;
  category_slug?: string;
  metadata?: Record<string, unknown>;
}) {
  if (typeof window === "undefined") return;
  try {
    void trackEvent({
      data: {
        event_name,
        anonymous_id: getAnon(),
        session_id: getSession(),
        path: extra?.path ?? window.location.pathname,
        referrer: document.referrer || null,
        business_slug: extra?.business_slug ?? null,
        category_slug: extra?.category_slug ?? null,
        metadata: extra?.metadata ?? {},
      },
    });
  } catch {
    /* swallow — telemetria nunca deve quebrar UI */
  }
}

/** Dispara um evento uma única vez no mount. */
export function useTrackOnce(event: TrackEventName, extra?: Parameters<typeof track>[1]) {
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    track(event, extra);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/** Session ping para DAU/WAU/MAU e duração média (a cada 60s, apenas com aba visível). */
export function useSessionPing() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    let stopped = false;
    function ping() {
      if (document.visibilityState !== "visible") return;
      track("session_ping");
    }
    ping();
    const int = window.setInterval(ping, 60_000);
    return () => {
      stopped = true;
      window.clearInterval(int);
      void stopped;
    };
  }, []);
}
